import { Button, Text } from '@shared/components';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { LoadShipmentForm } from '@shared/features/load-shipment-form';
import { isGmailUrl } from '@shared/features/sidekick-navigator/types';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useOrganizationFeatureFlag } from '@shared/hooks/useOrganizationFeatureFlag';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { TMS_SIDEKICK_ROUTE } from 'clerk_common/constants/urls/routes';
import {
  ChromeExtensionMessageType,
  FeatureFlagName,
} from 'clerk_common/types';
import { useState } from 'react';
import { LiaCheckSolid } from 'react-icons/lia';
import { useNavigate } from 'react-router-dom';
import { LoadPosting } from '../../features/LoadPosting/LoadPosting';

const ROOT = makeRootClassName('Home');
const el = makeElementClassNameFactory(ROOT);

const WIP_FEATURES = false;

export const Home = () => {
  const { defaultOrgId } = useMeContext();
  const { sendMessageToParent, parentUrl } = useExtensionMessagingContext();
  const navigate = useNavigate();

  const isLoadShipmentByIdEnabled = useOrganizationFeatureFlag({
    featureFlagName: FeatureFlagName.LOAD_SHIPMENT_BY_ID,
  });
  const onLoadShipment = (value: string) => {
    navigate(
      `${TMS_SIDEKICK_ROUTE}?shipmentReference=${encodeURIComponent(value)}`
    );
  };

  return (
    <div className={ROOT}>
      <div className={el`shadow`} />
      {isLoadShipmentByIdEnabled && (
        <div className="flex h-[46px] w-full flex-col items-center bg-gray-50">
          <LoadShipmentForm setShipmentReference={onLoadShipment} />
        </div>
      )}

      <div className={el`shadow`} />
      <div className={el`content`}>
        {WIP_FEATURES ? (
          <>
            <LoadPosting />
            <Button
              variant="secondary"
              onPress={() => {
                sendMessageToParent({
                  type: ChromeExtensionMessageType.FETCH_DOM_CONTENT,
                });
              }}
            >
              Test
            </Button>
          </>
        ) : isGmailUrl(parentUrl) ? (
          <GmailSidebarPage
            onOpen={() =>
              sendMessageToParent({
                type: ChromeExtensionMessageType.OPEN_GMAIL_SIDEBAR,
              })
            }
          />
        ) : (
          <NoMonitoringPage organizationId={defaultOrgId} url={parentUrl} />
        )}
      </div>
    </div>
  );
};

function GmailSidebarPage({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <Text type="body-md" isHeavy>
          Vooma is built into Gmail
        </Text>
        <Text type="body-sm" className="text-gray-500">
          Use the Vooma sidebar inside Gmail to work with the email or thread
          you&apos;re viewing.
        </Text>
        <img
          alt="Vooma icon highlighted in Gmail's right sidebar"
          className="w-48 rounded-lg border border-gray-200"
          src="/gmail-sidebar-location.png"
        />
        <Button variant="primary" size="small" onPress={onOpen}>
          Open Vooma in Gmail
        </Button>
      </div>
    </div>
  );
}

type NoMonitoringPageProps = {
  organizationId: string | null;
  url: string;
};

function NoMonitoringPage({ organizationId, url }: NoMonitoringPageProps) {
  const { track } = useAnalytics();
  const [hasRequestedMonitoring, setHasRequestedMonitoring] = useState(false);

  const onRequestMonitoring = () => {
    track('Request to monitor page with Chrome Extension', {
      organizationId,
      url,
    });
    setHasRequestedMonitoring(true);
  };

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Text type="body-lg" className="text-center">
          💤
        </Text>
        <Text type="body-md" className="text-center text-gray-500">
          Vooma doesn't look at this site
        </Text>
        {organizationId && (
          <Button
            variant="secondary"
            onPress={onRequestMonitoring}
            size="small"
            isDisabled={hasRequestedMonitoring}
            icon={hasRequestedMonitoring ? <LiaCheckSolid /> : undefined}
          >
            {hasRequestedMonitoring
              ? 'Request sent'
              : 'Request support for this site'}
          </Button>
        )}
      </div>
    </div>
  );
}
